// src/components/sections/HeroSection/HeroSection.jsx
import React, { useState, useRef, useEffect } from "react";
import BackgroundCardWall from "./BackgroundCardWall";
import "./HeroSection.css";

const HeroSection = () => {
  const [showBeta, setShowBeta] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const testFlightLink = "https://testflight.apple.com/join/KVhJ4483";
  const copyTimeoutRef = useRef(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(testFlightLink);
      setCopied(true);

      // Clear previous timeout if any
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 2500);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section id="hero-section" className="hero-section">
      <div className="hero-container">
        {/* LEFT SIDE: Text + CTA */}
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-text">AI AUDIO GENERATION</span>
          </div>

          <h1 className="hero-title">
            <span className="title-gradient">TURN YOUR IDEAS </span>
            <br />
            INTO ENGAGING PODCASTS
          </h1>

          {!showBeta ? (
            <button
              type="button"
              className="hero-cta"
              onClick={() => setShowBeta(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              <span className="cta-text">Download</span>
            </button>
          ) : (
            <div className="beta-reveal">
              <h2 className="beta-title">Join the Beta</h2>

              <div className="beta-actions">
                <a
                  href={testFlightLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="beta-btn primary"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  See TestFlight
                </a>

                <button
                  type="button"
                  className={`beta-btn secondary ${copied ? "copied" : ""}`}
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>Copied</span>
                    </>
                  ) : (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      <span>Copy Link</span>
                    </>
                  )}
                </button>
              </div>

              <button
                type="button"
                className="beta-info"
                onClick={() => setShowModal(true)}
              >
                What&apos;s TestFlight?
              </button>
            </div>
          )}

          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">3K+</span>
              <span className="stat-label">audio created</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-number">50+</span>
              <span className="stat-label">creators</span>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: Card wall */}
        <div className="hero-wall-container">
          <div className="wall-perspective-wrapper">
            <BackgroundCardWall count={24} />
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal-box"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-title">TestFlight Beta</h3>
            <p className="modal-text">
              Get early access and help shape our app. By joining, you agree to
              our Terms of Service and Privacy Policy.
            </p>
            <button
              type="button"
              className="modal-btn"
              onClick={() => setShowModal(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
